import { Button } from '@ps-design-system/components/Button';
import { CheckboxField } from '@ps-design-system/components/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@ps-design-system/components/ExpandableContent';
import { Text } from '@ps-design-system/components/Text';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useCheckoutContext } from '../../../CheckoutContextProvider';
import { GeneralRules } from './GeneralRules';
import { ProductionRules } from './ProductionRules';
import { SurveillanceRules } from './SurveillanceRules';

export const HostRulesSection = ({ urlPrefix, disabled }) => {
  const { listing } = useCheckoutContext();

  const [isExpanded, setIsExpanded] = React.useState(false);

  const generalRules = listing?.rules || '';
  const surveillanceInfo = listing?.surveillance_info || {};
  const productionDetails = listing?.production_details || {};

  return (
    <>
      <Text typeface="headline-2-m" element="h4" style={{ margin: 0 }}>
        <FormattedMessage
          id="booking_details_form.review_host_rules"
          defaultMessage="Review host rules"
        />
      </Text>

      <div style={{ margin: '24px 0' }}>
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          {!isExpanded && (
            // max height is 4 lines plus 12px gap
            <div style={{ maxHeight: '108px', overflow: 'hidden' }}>
              <GeneralRules generalRules={generalRules} />
            </div>
          )}
          <CollapsibleContent>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <GeneralRules generalRules={generalRules} />

              <div>
                <Text typeface="body-2-m">
                  {listing?.min_age > 0 ? (
                    <FormattedMessage
                      id="host_rules.min_age"
                      defaultMessage="Ages {minAge}+"
                      values={{ minAge: listing?.min_age }}
                    />
                  ) : (
                    <FormattedMessage
                      id="host_rules.all_ages"
                      defaultMessage="All ages are allowed in the space"
                    />
                  )}
                </Text>
              </div>

              {listing?.use_type === 'Production' && (
                <ProductionRules
                  productionDetails={productionDetails}
                  minAge={listing?.min_age}
                />
              )}

              <SurveillanceRules
                surveillanceInfo={surveillanceInfo}
                urlPrefix={urlPrefix}
              />
            </div>
          </CollapsibleContent>

          <div style={{ marginTop: '12px' }}>
            <CollapsibleTrigger asChild>
              <Button variant="underline">
                <Text typeface="body-2">
                  {isExpanded ? (
                    <FormattedMessage
                      id="host_rules.hide"
                      defaultMessage="Hide"
                    />
                  ) : (
                    <FormattedMessage
                      id="host_rules.read_more"
                      defaultMessage="Read more"
                    />
                  )}
                </Text>
              </Button>
            </CollapsibleTrigger>
          </div>
        </Collapsible>
      </div>

      <CheckboxField
        testId="host-rules-accepted-checkbox"
        variant="box"
        name="hostRulesAccepted"
        disabled={disabled}
        description={
          <FormattedMessage
            id="booking_details_form.host_rules_accept"
            defaultMessage="I accept and understand the host rules and disclosures."
          />
        }
      />
    </>
  );
};

HostRulesSection.displayName = 'HostRulesSection';
HostRulesSection.propTypes = {
  urlPrefix: PropTypes.string
};
